import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CANONICAL = 'https://www.utahrealtyphotos.com/credit';

/**
 * /credit — public credit pack page.
 *
 * WHY THIS EXISTS: credit packs replaced the subscription tiers, but the whole
 * offer lived behind a login. A prospect browsing the site, or anyone Thomas
 * mentions it to on a cold call, had no way to see it. `/subscriptions` also
 * redirected to `/services`, which says nothing about volume pricing — so the
 * old offer was removed without the new one being findable anywhere public.
 *
 * This page is the destination for that redirect, a URL that can be said out
 * loud on a call, and an indexable surface so answer engines can cite the offer.
 *
 * Prices are fetched LIVE from /api/credit-packs (a public endpoint). Nothing
 * here is hardcoded — the same discipline that stopped /services advertising
 * $175 against a $199 charge.
 */
function CreditPage() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    updatePageMeta(metaFor('/credit'));

    addBreadcrumbSchema([
      { name: 'Home', url: 'https://www.utahrealtyphotos.com/' },
      { name: 'Credit', url: CANONICAL }
    ]);

    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Does photography credit expire?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Credit never expires and carries no dormancy, inactivity or service fees. Use it whenever you have a listing.'
          }
        },
        {
          '@type': 'Question',
          name: 'What can credit be spent on?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Anything — full packages, individual add-ons like drone or virtual staging, or a standalone 3D tour. Credit applies automatically to your next invoice.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is there a monthly commitment?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Credit is a one-time purchase with no recurring billing. You are never charged in a month when you have no listings.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do brokerages get a better rate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Brokerages and high-volume agents can arrange a custom volume rate on a single balance the whole office draws against, invoiced to the brokerage. Contact Utah Realty Photos at (801) 210-0281 for a quote.'
          }
        }
      ]
    });

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/credit-packs`);
        if (!cancelled) setPacks(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (e) {
        // Soft default: the page still explains the offer without a price grid.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const steps = [
    ['Buy credit up front', 'Pick an amount. The larger the pack, the bigger the discount on everything you book.'],
    ['Book as normal', 'Nothing about scheduling changes. Book any package or add-on, whenever you have a listing.'],
    ['Credit applies itself', 'Your balance comes off each invoice automatically. No codes to remember, nothing to claim.']
  ];

  return (
    <div style={{ paddingTop: '140px', minHeight: '60vh' }}>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white', padding: '60px 0', textAlign: 'center'
      }}>
        <div className="container">
          <p style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>
            For agents who shoot often
          </p>
          <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>
            Pay ahead, pay less
          </h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.85, maxWidth: '640px', margin: '0 auto' }}>
            Buy photography credit up front and save on every listing. No monthly commitment,
            no expiry, no fees.
          </p>
        </div>
      </section>

      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 1.5rem' }}>

          {/* How it works */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', marginBottom: '3rem' }}>
            {steps.map(([title, body], i) => (
              <div key={title}>
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%', background: '#c9a96e',
                  color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.9rem', marginBottom: '10px'
                }}>{i + 1}</div>
                <h3 style={{ fontSize: '1.05rem', margin: '0 0 6px', color: '#1a1a2e' }}>{title}</h3>
                <p style={{ margin: 0, color: '#4b5563', fontSize: '0.95rem', lineHeight: 1.6 }}>{body}</p>
              </div>
            ))}
          </div>

          {/* Live packs */}
          <h2 style={{ fontSize: '1.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '1rem' }}>
            Credit packs
          </h2>

          {loading ? (
            <p style={{ color: '#6b7280' }}>Loading current rates&hellip;</p>
          ) : packs.length === 0 ? (
            <p style={{ color: '#4b5563', lineHeight: 1.7 }}>
              Credit is available in several sizes, with a larger discount the more you buy up front.
              Call <strong>(801) 210-0281</strong> and we&rsquo;ll walk you through the current rates.
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
              {packs.map((p) => (
                <div key={p.id} style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: '12px', padding: '1.25rem', borderLeft: '4px solid #c9a96e'
                }}>
                  <div style={{ fontSize: '1.45rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
                    ${Number(p.price).toFixed(0)}
                    <span style={{ fontSize: '0.95rem', fontWeight: 500, color: '#6b7280' }}> &rarr; </span>
                    <span style={{ color: '#059669' }}>${Number(p.totalCredit).toFixed(0)}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                    {p.effectiveDiscountPercent}% off everything you book
                  </div>
                </div>
              ))}
            </div>
          )}

          <p style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6, marginBottom: '2.5rem' }}>
            Credit never expires and carries no fees. Bonus credit can&rsquo;t be combined with
            promotional codes on the same invoice; purchased credit always applies.
          </p>

          {/* Brokerage */}
          <div style={{
            background: '#1a1a2e', color: 'white', borderRadius: '12px',
            padding: '1.75rem', marginBottom: '2.5rem'
          }}>
            <h2 style={{ fontSize: '1.35rem', fontFamily: "'Playfair Display', serif", margin: '0 0 8px' }}>
              Brokerages and teams
            </h2>
            <p style={{ margin: '0 0 1rem', opacity: 0.85, lineHeight: 1.7 }}>
              One balance your whole office draws against, at a custom volume rate, invoiced to the
              brokerage rather than to individual agents. No seats, no per-agent quotas, no monthly
              minimum &mdash; agents just book, and it comes off the balance.
            </p>
            <Link to="/contact" style={{
              display: 'inline-block', padding: '12px 28px', background: '#c9a96e',
              color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: 600
            }}>
              Request a volume rate
            </Link>
          </div>

          {/* CTA */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/client/portal" style={{
              padding: '14px 32px', background: '#c9a96e', color: 'white',
              borderRadius: '6px', textDecoration: 'none', fontWeight: '600'
            }}>
              Buy Credit
            </Link>
            <Link to="/services" style={{
              padding: '14px 32px', border: '2px solid #1a1a2e', color: '#1a1a2e',
              borderRadius: '6px', textDecoration: 'none', fontWeight: '500'
            }}>
              See Packages &amp; Pricing
            </Link>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '1rem' }}>
            Buying credit needs an account &mdash; it takes a minute, and it&rsquo;s the same login
            you&rsquo;ll use for your galleries.
          </p>

        </div>
      </section>
    </div>
  );
}

export default CreditPage;
